import { getAnchorId, getComponentMargin } from '@/utils/builderUtils';
import { carouselInfoType } from '@/components/Carousel/types';
import CarouselProvider from '@/components/Carousel/CarouselProvider';
import beTwMerge from '@/utils/beTwMerge';
import RichText from '@/components/RichText/RichText';
import Button from '@/components/homepage/Button';
import Carousel from '@/components/Carousel/Carousel';
import useMediaQuery from '@/hooks/useMediaQuery';

const categoryCarouselImage = src => [
  {
    src,
    size: { width: 297, aspectRatio: 768 / 890 },
    breakpoint: 320,
  },
  {
    src,
    size: { width: 443, aspectRatio: 768 / 890 },
    breakpoint: 767,
  },
  {
    src,
    size: { width: 280, aspectRatio: 768 / 890 },
    breakpoint: 768,
  },
  {
    src,
    size: { width: 372, aspectRatio: 768 / 890 },
    breakpoint: 1023,
  },
  {
    src,
    size: { width: 262, aspectRatio: 768 / 890 },
    breakpoint: 1024,
  },
  {
    src,
    size: { width: 443, aspectRatio: 768 / 890 },
    breakpoint: 1440,
  },
];

export default function HomepageCarousel({ OptionalHeader, optionalSubHeader, cta, items, anchorLinkID, margin }) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const { formattedItems, hoverImages } = items
    ? items.reduce(
        (acc, item) => {
          acc.formattedItems.push({
            name: item.name,
            description: item.description,
            link: item.link ? item.link : null,
            imgSrcs: categoryCarouselImage(item.imgSrcs),
            altText: item.altText,
            gaEvents: item.gaEvents,
          });

          acc.hoverImages.push({
            hoverImage:
              item.hoverImage !== undefined && item.hoverImage !== null ? categoryCarouselImage(item.hoverImage) : null,
            hoverImageAlt: item.HoverImageAltText || null,
          });

          return acc;
        },
        { formattedItems: [], hoverImages: [] }
      )
    : { formattedItems: [], hoverImages: [] };

  const carouselInfo = {
    name: 'category-carousel',
    type: carouselInfoType.category,
  };

  const isSubHeaderEmpty =
    !optionalSubHeader ||
    optionalSubHeader.length === 0 ||
    optionalSubHeader === ' ' ||
    optionalSubHeader === '&nbsp;' ||
    optionalSubHeader === '' ||
    optionalSubHeader === '<p><br /></p>' ||
    optionalSubHeader === '<p><br /></p>\n' ||
    optionalSubHeader === '<p><br /></p>' ||
    optionalSubHeader === '<p><br></p>';

  const moreDescriptiveAriaLabel = (
    headerTitle: string | undefined,
    href: string | undefined,
    label: string
  ): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (headerTitle) {
        return `${headerTitle} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')} page`;
    }
    return label || '';
  };

  return (
    <div {...getAnchorId(anchorLinkID)}>
      <CarouselProvider hoverImages={hoverImages}>
        <div
          className={beTwMerge(
            `tw-mx-4 lg:tw-mx-8 min-[1504px]:tw-max-w-[1440px] min-[1504px]:tw-mx-auto`,
            getComponentMargin(margin)
          )}
        >
          {OptionalHeader && (
            <div
              className={beTwMerge(
                'tw-flex md:tw-justify-between tw-items-center ',
                !isSubHeaderEmpty ? 'tw-mb-[8px] desktop:tw-mb-[16px]' : 'tw-mb-8'
              )}
            >
              <h2 className={beTwMerge('tw-text-left tw-mb-0 ')}>{OptionalHeader}</h2>

              {cta && cta.label && (
                <Button
                  className="tw-hidden md:tw-block"
                  href={cta.link}
                  type="link"
                  label={cta.label}
                  ariaLabel={moreDescriptiveAriaLabel(OptionalHeader, cta.link, cta.label)}
                />
              )}
            </div>
          )}

          {!isSubHeaderEmpty && <RichText content={optionalSubHeader} className="tw-mb-8" />}

          <Carousel
            info={carouselInfo}
            items={formattedItems}
            slidesToShow={isDesktop ? Math.min(formattedItems?.length || 0, 6) : 1.5}
            customItemStyles={{
              arrowPositions: 'tw-top-[130px] md:tw-top-[166px] lg:tw-top-[calc(305px/2)] xl:tw-top-[calc(445px/2)]',
              container: '',
              image: 'tw-w-full tw-h-full tw-object-cover !tw-aspect-[384/445]',
            }}
          />

          {cta && cta.label && (
            <div className="tw-flex tw-justify-center tw-mt-6 md:tw-mt-0">
              <Button
                ariaLabel={moreDescriptiveAriaLabel(OptionalHeader, cta.link, cta.label)}
                className="sm:tw-hidden tw-block"
                href={cta.link}
                type="link"
                label={cta.label}
              />
            </div>
          )}
        </div>
      </CarouselProvider>
    </div>
  );
}
